import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useSpamSchutz, Honigtopf } from "./SpamSchutz";

const ContactModal = ({ isOpen, onClose, pageSource, buttonContext, buttonText }) => {
  const { t } = useLanguage();
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errors, setErrors] = useState({});

  // Context mapping for intelligent auto-fill
  const getContextualMessage = (pageSource, buttonContext, buttonText) => {
    const currentUrl = window.location.href;
    
    // Generic button texts that don't provide specific context
    const genericButtons = [
      'Mehr dazu',
      'Learn more',
      'Erstgespräch buchen',
      'Kostenloses Erstgespräch buchen',
      'Kostenloses Beratungsgespräch buchen',
      'Gespräch vereinbaren',
      'Book Consultation'
    ];
    
    // Check if button text is generic
    const isGenericButton = genericButtons.some(generic => 
      buttonText && buttonText.toLowerCase().includes(generic.toLowerCase())
    );
    
    // For Homepage Hero Section, always use the specific German message
    if (pageSource === 'Homepage' && buttonContext === 'Hero Section') {
      return 'Jetzt KI-Transformation starten - unverbindliches Gespräch buchen';
    }
    
    // If button is specific, use button text
    if (!isGenericButton && buttonText) {
      return `Interesse an: ${buttonText}`;
    }
    
    // Otherwise use contextual information based on page and section
    const contextMap = {
      'Homepage': {
        'Hero Section': 'Jetzt KI-Transformation starten - unverbindliches Gespräch buchen',
        'CTA Section - Resources': 'Interesse an: Kostenloses Erstgespräch für KI-Beratung',
        'Solutions': 'Interesse an: KI-Lösungen für mein Unternehmen'
      },
      'KI-Akademie': {
        'Hero Section': 'Interesse an: KI-Akademie und strategische Planung',
        'Coaching Services': 'Interesse an: KI-Akademie für Unternehmensstrategie',
        'Weitere Beratungsfelder': 'Interesse an: Erweiterte KI-Beratungsleistungen'
      },
      'KI-Transformation': {
        'Hero Section': 'Interesse an: Ganzheitliche KI-Transformation',
        'CTA Section': 'Interesse an: KI-Transformationsstrategie für mein Unternehmen'
      },
      'KI-Coaching': {
        'Hero Section': 'Interesse an: KI-Coaching und KI-Beirat',
        'Coaching Services': 'Interesse an: Individuelles KI-Coaching'
      },
      'KI-Workshops': {
        'Hero Section': 'Interesse an: KI-Workshops für mein Team',
        'Workshop Services': 'Interesse an: Maßgeschneiderte KI-Workshops'
      },
      'KI-Anwendungen': {
        'Hero Section': 'Interesse an: KI-Anwendungen entwickeln',
        'CTA Section': 'Interesse an: KI-Projekte umsetzen'
      },
      'KI-Plattform': {
        'Hero Section': 'Interesse an: AIWizz KI-Plattform',
        'CTA Section': 'Interesse an: AIWizz Demo und Beratung'
      },
      'Über uns': {
        'CTA Section': 'Interesse an: Gespräch über KI-Möglichkeiten'
      },
      'Expertise': {
        'CTA Section': 'Interesse an: KI-Beratung basierend auf unserer Expertise'
      },
      'Whitepapers': {
        'CTA Section': 'Interesse an: KI-Beratung basierend auf Whitepaper-Inhalten'
      },
      'Enablement': {
        'CTA Section': 'Interesse an: KI-Enablement Programm'
      },
      'Lösungen': {
        'CTA Section': 'Interesse an: KI-Lösungen für mein Unternehmen'
      },
      'AI Use Cases': {
        'CTA Section': 'Interesse an: KI-Erfolgsgeschichten für mein Unternehmen'
      }
    };
    
    // Get contextual message
    if (contextMap[pageSource] && contextMap[pageSource][buttonContext]) {
      return contextMap[pageSource][buttonContext];
    }
    
    // Fallback based on page source only
    const pageContextMap = {
      'KI-Akademie': 'Interesse an: KI-Akademie für mein Unternehmen',
      'KI-Transformation': 'Interesse an: KI-Transformation in meinem Unternehmen',
      'KI-Coaching': 'Interesse an: KI-Coaching und Beratung',
      'KI-Workshops': 'Interesse an: KI-Workshops für mein Team',
      'KI-Anwendungen': 'Interesse an: KI-Anwendungen entwickeln',
      'KI-Plattform': 'Interesse an: AIWizz KI-Plattform',
      'Über uns': 'Interesse an: Mehr über Brovko erfahren',
      'Homepage': 'Interesse an: KI-Akademie für mein Unternehmen'
    };
    
    return pageContextMap[pageSource] || 'Interesse an: KI-Akademie für mein Unternehmen';
  };

  // Reset form when modal opens (no auto-fill for message field)
  useEffect(() => {
    if (isOpen) {
      // Reset form but keep message field empty for user input
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setErrors({});
      setShowSuccess(false);
      setShowError(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('contact.modal.error.firstName');
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('contact.modal.error.lastName');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact.modal.error.email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact.modal.error.email');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setShowError(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          ...formData,
          pageSource,
          buttonContext,
          buttonText,
          pageUrl: window.location.href,
          timestamp: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setShowError(true);
      }
    } catch (error) {
      // Error logged for development only
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: ''
    });
    setErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-slate-900 rounded-xl p-8 max-w-md w-full mx-4 border border-slate-700">
          <div className="text-center">
            <div className="bg-green-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">{t('contact.modal.success.title')}</h3>
            <p className="text-gray-300 mb-6">
              {t('contact.modal.success.text')}
            </p>
            <Button 
              onClick={handleClose}
              className="bg-teal-400 hover:bg-teal-500 text-slate-900 w-full"
            >
              {t('contact.modal.cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-900 rounded-xl p-8 max-w-md w-full mx-4 border border-slate-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">{t('contact.modal.title')}</h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <p className="text-gray-300 mb-6">
          {t('contact.modal.subtitle')}
        </p>

        {showError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-red-400 text-sm">
                {t('contact.modal.error.text')}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Honigtopf {...honigtopf} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('contact.modal.firstName')} <span className="text-red-400">*</span>
              </label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`bg-slate-800 border-slate-600 text-white ${errors.firstName ? 'border-red-500' : ''}`}
                placeholder="Max"
              />
              {errors.firstName && (
                <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('contact.modal.lastName')} <span className="text-red-400">*</span>
              </label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`bg-slate-800 border-slate-600 text-white ${errors.lastName ? 'border-red-500' : ''}`}
                placeholder="Mustermann"
              />
              {errors.lastName && (
                <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              {t('contact.modal.email')} <span className="text-red-400">*</span>
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`bg-slate-800 border-slate-600 text-white ${errors.email ? 'border-red-500' : ''}`}
              placeholder="max.mustermann@beispiel.de"
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              {t('contact.modal.phone')}
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="bg-slate-800 border-slate-600 text-white"
              placeholder="+49 123 456789"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              {t('contact.modal.message')}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              placeholder={t('contact.modal.placeholder')}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-slate-600 text-gray-300 hover:bg-slate-800"
              disabled={isSubmitting}
            >
              {t('contact.modal.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-teal-400 hover:bg-teal-500 text-slate-900"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
                  {t('contact.modal.submitting')}
                </>
              ) : (
                <>
                  <Calendar className="mr-2 h-4 w-4" />
                  {t('contact.modal.submit')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;